import { useMutation } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import type { ReactNode } from 'react'
import { useEffect, useRef, useState } from 'react'
import { reservationTerminal, useReservations } from '@/entities/capacity'
import type { FleetUnitView } from '@/entities/fleet'
import { startDemoCallMutation, startTestCallMutation, useFleetOverview } from '@/entities/fleet'
import { CallLink, hangupCallMutation } from '@/entities/metering'
import { scenarioLangCode, scenarioLangLabel, useScenarios } from '@/entities/scenario'
import { useVoices } from '@/entities/voice'
import { cn, errorCode, errorMessage, formatClock } from '@/shared/lib'
import { Button } from '@/shared/ui/button'
import { Chip } from '@/shared/ui/chip'
import { SelectField, TextField } from '@/shared/ui/field'
import { Icon } from '@/shared/ui/icon'
import { PageHeader } from '@/shared/ui/page-header'
import { PillTabs } from '@/shared/ui/pill-tabs'

export const Route = createFileRoute('/demo-call')({
  component: DemoCallPage,
})

type Line = 'webrtc' | 'sip'
type Phase = 'idle' | 'connecting' | 'in-call' | 'ended' | 'mock' | 'error'

// Бэк принимает номер только в E.164 — ту же форму проверяем до запроса.
const E164 = /^\+[1-9][0-9]{6,14}$/

// Демо целится в C-юнит ячейки — выбирать есть смысл только готовый.
function cellCReady(units: FleetUnitView[], cUnitId: string): boolean {
  return units.find((u) => u.id === cUnitId)?.status === 'ready'
}

// Экспорт нужен компонентному тесту: сам роут его собирает из этого же модуля.
export function DemoCallPage() {
  const [line, setLine] = useState<Line>('webrtc')
  const [scenarioId, setScenarioId] = useState('')
  const [voiceId, setVoiceId] = useState('')

  const { scenarios } = useScenarios()
  // Голос выбирается из каталога: в спеку звонка уезжает его id, руками id не вводят.
  const { voices } = useVoices()
  const readyVoices = voices.filter((v) => v.status === 'ready')
  const scenario = scenarios.find((s) => s.id === scenarioId)

  // Сценарий несёт голос по умолчанию — подставляем его, оператор может сменить.
  const pickScenario = (id: string) => {
    setScenarioId(id)
    setVoiceId(scenarios.find((s) => s.id === id)?.default_voice_id ?? '')
  }

  const fields = (
    <div className="flex w-full flex-col gap-4">
      <SelectField
        label="Сценарий"
        required
        value={scenarioId}
        onChange={(e) => pickScenario(e.target.value)}
        hint={
          scenarios.length === 0
            ? 'Каталог сценариев пуст — заведи сценарий в разделе «Сценарии»'
            : 'Разговор задаёт сценарий: промпт, язык звонка и голос по умолчанию едут из него'
        }
      >
        <option value="" disabled>
          Выбери сценарий
        </option>
        {scenarios.map((s) => (
          <option key={s.id} value={s.id}>
            {s.name} · {scenarioLangCode(s.language)}
          </option>
        ))}
      </SelectField>

      {scenario && (
        <p className="-mt-2 flex items-center gap-2">
          <Chip tone="accent">{scenarioLangLabel(scenario.language)}</Chip>
          <span className="text-caption text-faint">
            язык звонка фиксируется сценарием и внутри звонка не меняется
          </span>
        </p>
      )}

      <SelectField
        label="Голос"
        value={voiceId}
        onChange={(e) => setVoiceId(e.target.value)}
        hint={
          readyVoices.length === 0
            ? 'Каталог голосов пуст — движок возьмёт дефолтный'
            : 'Голос сценария можно переопределить'
        }
      >
        <option value="">Дефолтный голос движка</option>
        {readyVoices.map((voice) => (
          <option key={voice.id} value={voice.id}>
            {voice.name}
          </option>
        ))}
      </SelectField>

      {scenarioId === '' && (
        <p className="rounded-control bg-warn/10 px-4 py-2.5 text-body-sm text-warn">
          Звонок идёт только по сценарию — выбери его из каталога, свободного промпта нет.
        </p>
      )}
    </div>
  )

  return (
    <>
      <PageHeader
        title="Демо-звонок"
        description="Звонок боту по сценарию из каталога — язык разговора едет из сценария; линия — WebRTC из браузера или SIP на номер"
      />

      <div className="mx-auto flex w-full max-w-xl flex-col gap-4">
        <PillTabs
          aria-label="Линия звонка"
          value={line}
          onChange={setLine}
          options={[
            { value: 'webrtc', label: 'WebRTC · из браузера' },
            { value: 'sip', label: 'SIP · на номер' },
          ]}
          className="justify-center"
        />

        {line === 'webrtc' ? (
          <WebrtcCall scenarioId={scenarioId} voiceId={voiceId} fields={fields} />
        ) : (
          <SipCall scenarioId={scenarioId} voiceId={voiceId} fields={fields} />
        )}
      </div>
    </>
  )
}

function WebrtcCall({
  scenarioId,
  voiceId,
  fields,
}: {
  scenarioId: string
  voiceId: string
  fields: ReactNode
}) {
  const [phase, setPhase] = useState<Phase>('idle')
  const [record, setRecord] = useState(false)
  const [cellId, setCellId] = useState('')
  const [callId, setCallId] = useState<string | null>(null)
  const [failure, setFailure] = useState<string | null>(null)
  const [startedAt, setStartedAt] = useState<number | null>(null)
  const [elapsed, setElapsed] = useState(0)

  const pcRef = useRef<RTCPeerConnection | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const audioRef = useRef<HTMLAudioElement>(null)

  const demoCall = useMutation(startDemoCallMutation())
  const hangup = useMutation(hangupCallMutation())

  // Ячейки поднимаются заранее и живут после демо — выбор цели явный.
  const { cells, units } = useFleetOverview()

  useEffect(() => {
    if (phase !== 'in-call' || startedAt == null) return
    const t = setInterval(() => setElapsed(Date.now() - startedAt), 500)
    return () => clearInterval(t)
  }, [phase, startedAt])

  const cleanup = () => {
    pcRef.current?.close()
    pcRef.current = null
    for (const track of streamRef.current?.getTracks() ?? []) track.stop()
    streamRef.current = null
  }

  useEffect(
    () => () => {
      pcRef.current?.close()
      for (const track of streamRef.current?.getTracks() ?? []) track.stop()
    },
    [],
  )

  const start = async () => {
    setFailure(null)
    setPhase('connecting')
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      streamRef.current = stream

      const pc = new RTCPeerConnection()
      pcRef.current = pc
      for (const track of stream.getTracks()) pc.addTrack(track, stream)
      pc.ontrack = (event) => {
        if (audioRef.current && event.streams[0]) {
          audioRef.current.srcObject = event.streams[0]
        }
      }

      const offer = await pc.createOffer({ offerToReceiveAudio: true })
      await pc.setLocalDescription(offer)

      const res = await demoCall.mutateAsync({
        body: {
          sdp_offer: offer.sdp ?? '',
          scenario_id: scenarioId,
          ...(cellId ? { cell_id: cellId } : {}),
          ...(voiceId ? { voice_id: voiceId } : {}),
          ...(record ? { record: true } : {}),
        },
      })
      setCallId(res.call_id)

      try {
        await pc.setRemoteDescription({ type: 'answer', sdp: res.sdp_answer })
        setStartedAt(Date.now())
        setElapsed(0)
        setPhase('in-call')
      } catch {
        // Мок отдаёт заглушечный SDP — медиа-канал не поднимется.
        cleanup()
        setPhase('mock')
      }
    } catch (e) {
      cleanup()
      setFailure(errorMessage(e))
      setPhase('error')
    }
  }

  // Закрыть RTCPeerConnection мало: звонок живёт на движке, пока ему не придёт
  // call.cmd.hangup — его публикует бэкенд по этому запросу.
  const hangUp = () => {
    if (callId) hangup.mutate({ path: { call_id: callId } })
    cleanup()
    setPhase('ended')
  }

  const reset = () => {
    hangup.reset()
    setCallId(null)
    setFailure(null)
    setPhase('idle')
  }

  const idle = phase === 'idle' || phase === 'ended' || phase === 'mock' || phase === 'error'

  return (
    <div className="flex flex-col items-center gap-6 rounded-panel bg-panel p-8">
      <div
        className={cn(
          'flex size-24 items-center justify-center rounded-full transition-colors',
          phase === 'in-call'
            ? 'bg-grad-active text-white'
            : phase === 'connecting'
              ? 'animate-pulse bg-accent/20 text-accent'
              : 'bg-raise text-muted',
        )}
      >
        <Icon name="mic" size={38} />
      </div>

      <div className="text-center">
        {phase === 'idle' && <p className="text-body-md text-muted">Готов к звонку</p>}
        {phase === 'connecting' && <p className="text-body-md text-muted">Соединяем…</p>}
        {phase === 'in-call' && (
          <>
            <p className="text-display font-light tabular-nums">{formatClock(elapsed)}</p>
            <p className="mt-1 text-body-sm text-muted">Разговор идёт · {callId}</p>
          </>
        )}
        {phase === 'ended' && (
          <>
            <p className="text-body-md text-muted">Звонок завершён</p>
            {hangup.isError && (
              <p className="mt-1 max-w-sm text-body-sm text-danger">
                Разрыв не доехал до движка: {errorMessage(hangup.error)}
              </p>
            )}
          </>
        )}
        {phase === 'mock' && (
          <p className="max-w-sm text-body-sm text-muted">
            Запрос прошёл (call <span className="font-mono">{callId}</span>), но в mock-режиме
            медиа-канал не поднимается. Против реального бэкенда здесь будет живой разговор.
          </p>
        )}
        {phase === 'error' && (
          <p className="max-w-sm text-body-sm text-danger">{failure ?? 'Не получилось'}</p>
        )}
      </div>

      {idle && fields}
      {idle && (
        <div className="flex w-full flex-col gap-4">
          <SelectField
            label="Ячейка"
            value={cellId}
            onChange={(e) => setCellId(e.target.value)}
            hint={
              cells.length === 0
                ? 'Ячеек нет — «любая» возьмёт свободный операторский юнит, если он есть'
                : 'Явный выбор целит демо ровно в C-юнит этой ячейки, без перебора и подмены; занятая ячейка — честная ошибка'
            }
          >
            <option value="">Любая операторская ячейка</option>
            {cells.map((c) => (
              <option key={c.id} value={c.id} disabled={!cellCReady(units, c.c_unit_id)}>
                {c.pool} · {c.id.slice(0, 8)} ·{' '}
                {cellCReady(units, c.c_unit_id) ? 'C-юнит готов' : 'C-юнит не готов'}
              </option>
            ))}
          </SelectField>
        </div>
      )}
      {idle && <RecordToggle checked={record} onChange={setRecord} />}

      <div className="flex items-center gap-3">
        {idle && (
          <Button
            variant="accent"
            size="md"
            disabled={scenarioId === ''}
            onClick={() => void start()}
          >
            <Icon name="phone-call" size={18} />
            {phase === 'idle' ? 'Позвонить боту' : 'Позвонить ещё раз'}
          </Button>
        )}
        {phase === 'connecting' && (
          <Button pending onClick={() => undefined}>
            Соединяем…
          </Button>
        )}
        {phase === 'in-call' && (
          <Button variant="danger" onClick={hangUp}>
            Завершить
          </Button>
        )}
        {(phase === 'ended' || phase === 'error' || phase === 'mock') && (
          <Button onClick={reset}>Сбросить</Button>
        )}
      </div>

      {/* biome-ignore lint/a11y/useMediaCaption: live-аудио звонка, дорожки субтитров нет */}
      <audio ref={audioRef} autoPlay />
    </div>
  )
}

function SipCall({
  scenarioId,
  voiceId,
  fields,
}: {
  scenarioId: string
  voiceId: string
  fields: ReactNode
}) {
  const [phone, setPhone] = useState('')
  // Цель набора: '' — любая операторская ячейка (прямой вход), 'cell:<id>' —
  // явная ячейка, 'res:<id>' — очередь живой заявки (путь приложения).
  const [target, setTarget] = useState('')
  const [record, setRecord] = useState(false)

  const { cells, units } = useFleetOverview()
  const { reservations, refetch: refetchReservations } = useReservations({ activeOnly: true })
  const liveReservations = reservations.filter((r) => !reservationTerminal(r.status))

  const demoCall = useMutation(startDemoCallMutation())
  const testCall = useMutation({
    ...startTestCallMutation(),
    // Заявка умерла или ушла из пула между чтением списка и набором — список
    // перечитывается, чтобы цель выбиралась заново из живых.
    onError: (e) => {
      if (errorCode(e) === 'FLEET_RESERVATION_UNUSABLE') refetchReservations()
    },
  })
  const pending = demoCall.isPending || testCall.isPending
  const failure = demoCall.error ?? testCall.error
  const started = demoCall.data ?? testCall.data
  const viaQueue = target.startsWith('res:')
  // 422 FLEET_RESERVATION_UNUSABLE — ошибка цели, а не звонка: detail бэка
  // встаёт под селект, к полю, которое надо поменять.
  const targetError =
    errorCode(failure) === 'FLEET_RESERVATION_UNUSABLE' ? errorMessage(failure) : undefined

  const phoneTyped = phone.trim() !== ''
  const phoneValid = E164.test(phone.trim())
  const ready = scenarioId !== '' && phoneValid && !pending

  const dial = () => {
    const common = {
      scenario_id: scenarioId,
      phone: phone.trim(),
      ...(voiceId ? { voice_id: voiceId } : {}),
      ...(record ? { record: true } : {}),
    }
    if (viaQueue) {
      const r = liveReservations.find((x) => `res:${x.id}` === target)
      if (!r) return
      testCall.mutate({ body: { ...common, pool: r.pool, reservation: r.id } })
      return
    }
    demoCall.mutate({
      body: {
        ...common,
        line: 'sip',
        ...(target.startsWith('cell:') ? { cell_id: target.slice(5) } : {}),
      },
    })
  }

  return (
    <div className="flex flex-col gap-5 rounded-panel bg-panel p-8">
      <p className="text-body-sm text-muted">
        Набор идёт прямым входом движка выбранной ячейки — как веб-демо, только линией SIP. Путь
        очереди (ровно как звонит приложение) — выбором живой заявки.
      </p>

      {fields}

      <TextField
        label="Номер"
        required
        placeholder="+79001234567"
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        error={phoneTyped && !phoneValid ? 'Нужен формат E.164: +79001234567' : undefined}
        hint="Кому звонит бот"
      />

      <SelectField
        label="Цель"
        value={target}
        onChange={(e) => setTarget(e.target.value)}
        error={targetError}
        hint={
          cells.length === 0 && liveReservations.length === 0
            ? '«Любая» возьмёт свободный операторский юнит с зарегистрированной линией — ячеек и живых заявок сейчас нет'
            : 'Ячейка — прямой набор мимо очереди; заявка — задание в её очередь, как кладёт приложение'
        }
      >
        <option value="">Любая операторская ячейка</option>
        {cells.length > 0 && (
          <optgroup label="Ячейки — прямой набор">
            {cells.map((c) => (
              <option key={c.id} value={`cell:${c.id}`} disabled={!cellCReady(units, c.c_unit_id)}>
                {c.pool} · {c.id.slice(0, 8)} ·{' '}
                {cellCReady(units, c.c_unit_id) ? 'C-юнит готов' : 'C-юнит не готов'}
              </option>
            ))}
          </optgroup>
        )}
        {liveReservations.length > 0 && (
          <optgroup label="Заявки — через очередь">
            {liveReservations.map((r) => (
              <option key={r.id} value={`res:${r.id}`}>
                {r.id} · {r.pool} · готово {r.slots_ready}/{r.slots}
              </option>
            ))}
          </optgroup>
        )}
      </SelectField>

      <RecordToggle checked={record} onChange={setRecord} />

      <Button
        variant="accent"
        className="self-start"
        pending={pending}
        disabled={!ready}
        onClick={dial}
      >
        <Icon name="phone" size={18} />
        Позвонить на номер
      </Button>

      {failure != null && !targetError && (
        <p className="rounded-control bg-danger/10 px-4 py-2.5 text-body-sm text-danger">
          {errorMessage(failure)}
        </p>
      )}
      {started && (
        <div className="rounded-control bg-raise px-4 py-3 text-body-sm">
          <p>
            {viaQueue
              ? 'Задание принято — свободный юнит заявки набирает номер.'
              : 'Принято — юнит набирает номер.'}
          </p>
          <p className="mt-1 flex items-center gap-2 text-muted">
            Звонок: <CallLink id={started.call_id} />
          </p>
          <p className="mt-1 text-caption text-faint">
            Разбор появится в карточке звонка, когда движок пришлёт отчёт.
          </p>
        </div>
      )}
    </div>
  )
}

// Гейт записи — per-call: по умолчанию разговор не пишется, и слушать в карточке
// звонка будет нечего. Включённая запись уезжает в CallSpec.record.
function RecordToggle({
  checked,
  onChange,
}: {
  checked: boolean
  onChange: (next: boolean) => void
}) {
  return (
    <label className="grid cursor-pointer grid-cols-[auto_1fr] items-center gap-x-3 self-start rounded-control bg-raise px-4 py-3">
      <input
        type="checkbox"
        className="row-span-2 size-4 cursor-pointer accent-accent"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="text-body-sm">Записывать разговор</span>
      <span className="text-caption text-faint">
        Запись ляжет в карточку звонка — её можно будет послушать
      </span>
    </label>
  )
}
